import { zfd } from 'zod-form-data';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { checkUsPostalCode, customerInfoSchema } from '~/utils/schemas';

export const isDefectiveFormKey = 'isDefective';

const schema = zfd.formData(
  // reuse the shared address verification schema; email comes from the order
  customerInfoSchema
    .extend({
      // Unchecked boxes are absent from FormData; Zod 4.6 rejected that missing key
      // before checkbox coercion. Defaulting to false submits a non-defective withdrawal.
      [isDefectiveFormKey]: zfd.checkbox().default(false),
    })
    .check(checkUsPostalCode),
);

export const { action, useActionResult } = makeAction(
  async ({ context, formData, params: { store } }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    const parsed = schema.safeParse(formData);

    if (!parsed.success) {
      return {
        ok: false as const,
        message:
          'Something is not quite right with your address. Please check it and try again.',
      };
    }

    const { isDefective, firstName, lastName, address } = parsed.data;

    const verifiedAddress = {
      ...address,
      firstName,
      lastName,
      stateOrProvinceCode: address.stateOrProvinceCode ?? undefined,
      phone: address.phone ?? undefined,
    };

    try {
      const { claimExternalId } = await api.withdrawCrewOrder({
        params: {
          storefrontId: settings.storefrontId,
        },
        body: {
          storeOrderId: order.id,
          isDefective: !!isDefective,
          address: verifiedAddress,
        },
      });

      appStore.dispatch(appStore.actions.invalidateOrder());

      // ? The return was created successfully, redirect to the claim overview page
      return redirect(`/${store}/claim/overview/${claimExternalId}`);
    } catch {
      return {
        ok: false as const,
        message:
          'Sorry, we were not able to process your withdrawal. Please contact us for assistance.',
      };
    }
  },
);

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const order = context.get(crewOrderContext);
  const address = order.shippingAddress;

  return {
    email: order.email,
    address,
  };
});
